import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { api } from '@/api';
import { toast } from 'sonner';
import { 
  Bug, Lightbulb, Search, Clock, CheckCircle, AlertCircle, 
  MessageSquare, Eye, Send, Loader2, ThumbsUp, BarChart3,
  Mail, Users, Filter, ArrowUpDown, ExternalLink, Megaphone,
  Sparkles, AlertTriangle, XCircle, Calendar
} from 'lucide-react';

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  triaged: { label: 'Triaged', color: 'bg-purple-100 text-purple-700 border-purple-200' },
  in_progress: { label: 'In Progress', color: 'bg-yellow-100 text-yellow-700 border-yellow-200' },
  testing: { label: 'Testing', color: 'bg-orange-100 text-orange-700 border-orange-200' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700 border-green-200' },
  wont_fix: { label: "Won't Fix", color: 'bg-slate-100 text-slate-700 border-slate-200' },
  duplicate: { label: 'Duplicate', color: 'bg-slate-100 text-slate-700 border-slate-200' },
};

const PRIORITY_CONFIG = {
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle },
  high: { label: 'High', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: AlertCircle },
  medium: { label: 'Medium', color: 'bg-yellow-100 text-yellow-700 border-yellow-200', icon: Clock },
  low: { label: 'Low', color: 'bg-slate-100 text-slate-700 border-slate-200', icon: Clock },
};

const TYPE_CONFIG = {
  bug: { label: 'Bug', color: 'bg-red-100 text-red-700 border-red-200', icon: Bug },
  feature: { label: 'Feature', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Lightbulb },
  improvement: { label: 'Improvement', color: 'bg-green-100 text-green-700 border-green-200', icon: Sparkles },
};

export default function AdminFeedbackDashboard() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tickets');
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Dialogs
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
  const [announcementDialogOpen, setAnnouncementDialogOpen] = useState(false);
  
  // Announcements
  const [announcements, setAnnouncements] = useState([]);
  const [selectedTicketsForAnnouncement, setSelectedTicketsForAnnouncement] = useState([]);

  useEffect(() => {
    loadTickets();
    loadStats();
  }, [statusFilter, typeFilter, priorityFilter, page]);

  useEffect(() => {
    if (activeTab === 'announcements') {
      loadAnnouncements();
    }
  }, [activeTab]);

  const loadTickets = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      if (typeFilter) params.type = typeFilter;
      if (priorityFilter) params.priority = priorityFilter;
      if (searchQuery) params.search = searchQuery;
      
      const response = await api.feedback.adminGetTickets(params);
      setTickets(response.data.tickets || []);
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Failed to load tickets:', error);
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await api.feedback.adminGetStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadAnnouncements = async () => {
    try {
      const response = await api.feedback.getAnnouncements({});
      setAnnouncements(response.data.announcements || []);
    } catch (error) {
      console.error('Failed to load announcements:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadTickets();
  };

  const handleViewTicket = async (ticket) => {
    try {
      const response = await api.feedback.getTicket(ticket.id);
      setSelectedTicket(response.data);
      setTicketDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load ticket details');
    }
  };

  const handleUpdateTicket = async (ticketId, updates) => {
    try {
      await api.feedback.adminUpdateTicket(ticketId, updates);
      toast.success('Ticket updated');
      loadTickets();
      loadStats();
      
      // Refresh ticket details if open
      if (selectedTicket?.ticket?.id === ticketId) {
        const response = await api.feedback.getTicket(ticketId);
        setSelectedTicket(response.data);
      }
    } catch (error) {
      toast.error('Failed to update ticket');
    }
  };

  const toggleTicketSelection = (ticketId) => {
    setSelectedTicketsForAnnouncement(prev => 
      prev.includes(ticketId) 
        ? prev.filter(id => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="admin-feedback-dashboard">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Feedback Management</h1>
            <p className="text-slate-500">Manage bug reports, feature requests, and announcements</p>
          </div>
          <Button onClick={() => setAnnouncementDialogOpen(true)} className="gap-2">
            <Megaphone className="h-4 w-4" />
            Create Announcement
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <StatCard title="Total Tickets" value={stats.tickets?.total || 0} icon={MessageSquare} />
            <StatCard title="New" value={stats.tickets?.by_status?.new || 0} icon={AlertCircle} color="blue" />
            <StatCard title="In Progress" value={stats.tickets?.by_status?.in_progress || 0} icon={Clock} color="yellow" />
            <StatCard title="Completed" value={stats.tickets?.by_status?.completed || 0} icon={CheckCircle} color="green" />
            <StatCard title="Bugs" value={stats.tickets?.by_type?.bug || 0} icon={Bug} color="red" />
            <StatCard title="Features" value={stats.tickets?.by_type?.feature || 0} icon={Lightbulb} color="blue" />
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="tickets" className="gap-2">
              <MessageSquare className="h-4 w-4" />
              Tickets
            </TabsTrigger>
            <TabsTrigger value="announcements" className="gap-2">
              <Megaphone className="h-4 w-4" />
              Announcements
            </TabsTrigger>
          </TabsList>

          {/* Tickets Tab */}
          <TabsContent value="tickets" className="space-y-4">
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <form onSubmit={handleSearch} className="flex flex-wrap gap-3">
                  <div className="flex-1 min-w-[200px]">
                    <Input
                      placeholder="Search tickets..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={typeFilter || "all"} onValueChange={(v) => setTypeFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="bug">Bug</SelectItem>
                      <SelectItem value="feature">Feature</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={priorityFilter || "all"} onValueChange={(v) => setPriorityFilter(v === "all" ? "" : v)}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="Priority" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Priority</SelectItem>
                      {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                        <SelectItem key={key} value={key}>{config.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button type="submit" variant="secondary">
                    <Search className="h-4 w-4 mr-2" />
                    Search
                  </Button>
                </form>
              </CardContent>
            </Card>

            {/* Tickets Table */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <Card>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="w-8 p-3"></th>
                        <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">#</th>
                        <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Type</th>
                        <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Title</th>
                        <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Reporter</th>
                        <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Status</th>
                        <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Priority</th>
                        <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Votes</th>
                        <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Date</th>
                        <th className="text-left p-3 text-xs font-medium text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((ticket) => (
                        <tr key={ticket.id} className="border-b hover:bg-slate-50">
                          <td className="p-3">
                            <Checkbox
                              checked={selectedTicketsForAnnouncement.includes(ticket.id)}
                              onCheckedChange={() => toggleTicketSelection(ticket.id)}
                            />
                          </td>
                          <td className="p-3 font-mono text-sm">#{ticket.ticket_number}</td>
                          <td className="p-3">
                            <Badge className={TYPE_CONFIG[ticket.type]?.color || 'bg-slate-100'}>
                              {TYPE_CONFIG[ticket.type]?.label || ticket.type}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div className="max-w-xs truncate font-medium">{ticket.title}</div>
                          </td>
                          <td className="p-3 text-sm text-slate-600">{ticket.reporter_name}</td>
                          <td className="p-3">
                            <Select 
                              value={ticket.status} 
                              onValueChange={(v) => handleUpdateTicket(ticket.id, { status: v })}
                            >
                              <SelectTrigger className="w-[130px] h-8">
                                <Badge className={STATUS_CONFIG[ticket.status]?.color}>
                                  {STATUS_CONFIG[ticket.status]?.label}
                                </Badge>
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-3">
                            <Select 
                              value={ticket.priority} 
                              onValueChange={(v) => handleUpdateTicket(ticket.id, { priority: v })}
                            >
                              <SelectTrigger className="w-[110px] h-8">
                                <Badge className={PRIORITY_CONFIG[ticket.priority]?.color}>
                                  {PRIORITY_CONFIG[ticket.priority]?.label}
                                </Badge>
                              </SelectTrigger>
                              <SelectContent>
                                {Object.entries(PRIORITY_CONFIG).map(([key, config]) => (
                                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="p-3 text-sm">
                            {ticket.type === 'feature' && (
                              <span className="flex items-center gap-1 text-blue-600">
                                <ThumbsUp className="h-3 w-3" />
                                {ticket.vote_count}
                              </span>
                            )}
                          </td>
                          <td className="p-3 text-sm text-slate-500">
                            {new Date(ticket.created_at).toLocaleDateString()}
                          </td>
                          <td className="p-3">
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => handleViewTicket(ticket)}
                            >
                              <Eye className="h-4 w-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                
                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-4 border-t">
                    <Button 
                      variant="outline" 
                      disabled={page === 1}
                      onClick={() => setPage(p => p - 1)}
                    >
                      Previous
                    </Button>
                    <span className="text-sm text-slate-500">
                      Page {page} of {totalPages}
                    </span>
                    <Button 
                      variant="outline" 
                      disabled={page === totalPages}
                      onClick={() => setPage(p => p + 1)}
                    >
                      Next
                    </Button>
                  </div>
                )}
              </Card>
            )}
          </TabsContent>

          {/* Announcements Tab */}
          <TabsContent value="announcements" className="space-y-4">
            {announcements.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Megaphone className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">No announcements yet</h3>
                  <p className="text-slate-500 mb-4">
                    Create an announcement to notify users about new features or bug fixes
                  </p>
                  <Button onClick={() => setAnnouncementDialogOpen(true)}>
                    Create Announcement
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {announcements.map((announcement) => (
                  <Card key={announcement.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge className={announcement.is_sent ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>
                              {announcement.is_sent ? 'Sent' : 'Draft'}
                            </Badge>
                            <Badge variant="outline">{announcement.announcement_type}</Badge>
                          </div>
                          <h3 className="font-medium text-slate-900">{announcement.title}</h3>
                          {announcement.is_sent && (
                            <p className="text-sm text-slate-500 mt-1">
                              Sent to {announcement.total_recipients} users on {new Date(announcement.sent_at).toLocaleString()}
                            </p>
                          )}
                        </div>
                        {!announcement.is_sent && (
                          <Button 
                            size="sm"
                            onClick={() => handleSendAnnouncement(announcement.id)}
                          >
                            <Send className="h-4 w-4 mr-1" />
                            Send
                          </Button>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Ticket Detail Dialog */}
        <TicketDetailDialog
          open={ticketDialogOpen}
          onOpenChange={setTicketDialogOpen}
          ticket={selectedTicket}
          onUpdate={(updates) => handleUpdateTicket(selectedTicket?.ticket?.id, updates)}
        />

        {/* Announcement Dialog */}
        <AnnouncementDialog
          open={announcementDialogOpen}
          onOpenChange={setAnnouncementDialogOpen}
          selectedTickets={selectedTicketsForAnnouncement}
          onSuccess={() => {
            loadAnnouncements();
            setSelectedTicketsForAnnouncement([]);
          }}
        />
      </div>
    </DashboardLayout>
  );
}

function StatCard({ title, value, icon: Icon, color = 'slate' }) {
  const colorClasses = {
    slate: 'bg-slate-100 text-slate-600',
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    red: 'bg-red-100 text-red-600',
  };

  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-slate-500 uppercase">{title}</p>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
          </div>
          <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
            <Icon className="h-5 w-5" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function TicketDetailDialog({ open, onOpenChange, ticket, onUpdate }) {
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [internalNotes, setInternalNotes] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');

  useEffect(() => {
    if (ticket?.ticket) {
      setInternalNotes(ticket.ticket.internal_notes || '');
      setResolutionNotes(ticket.ticket.resolution_notes || '');
    }
  }, [ticket]);

  if (!ticket) return null;

  const ticketData = ticket.ticket;

  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      await api.feedback.addComment(ticketData.id, { 
        content: newComment, 
        is_internal: isInternal 
      });
      toast.success('Comment added');
      setNewComment('');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveNotes = () => {
    onUpdate({
      internal_notes: internalNotes,
      resolution_notes: resolutionNotes
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <span className="font-mono text-slate-500">#{ticketData.ticket_number}</span>
            <Badge className={TYPE_CONFIG[ticketData.type]?.color}>
              {TYPE_CONFIG[ticketData.type]?.label}
            </Badge>
            <Badge className={STATUS_CONFIG[ticketData.status]?.color}>
              {STATUS_CONFIG[ticketData.status]?.label}
            </Badge>
            <Badge className={PRIORITY_CONFIG[ticketData.priority]?.color}>
              {PRIORITY_CONFIG[ticketData.priority]?.label}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-4">
          {/* Main Content */}
          <div className="col-span-2 space-y-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">{ticketData.title}</h2>
              <p className="text-sm text-slate-500">
                By {ticketData.reporter_name} ({ticketData.reporter_email}) on {new Date(ticketData.created_at).toLocaleString()}
              </p>
            </div>

            <div className="bg-slate-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-slate-700 mb-2">Description</h4>
              <p className="text-slate-600 whitespace-pre-wrap">{ticketData.description}</p>
            </div>

            {ticketData.type === 'bug' && ticketData.steps_to_reproduce && (
              <div className="bg-slate-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-slate-700 mb-2">Steps to Reproduce</h4>
                <p className="text-slate-600 whitespace-pre-wrap">{ticketData.steps_to_reproduce}</p>
              </div>
            )}

            {/* Internal Notes (Admin only) */}
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-yellow-700 mb-2">Internal Notes (Admin only)</h4>
              <Textarea
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                placeholder="Add internal notes..."
                rows={3}
                className="bg-white"
              />
            </div>

            {/* Resolution Notes */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-sm font-medium text-green-700 mb-2">Resolution Notes (Visible to reporter)</h4>
              <Textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Describe the resolution..."
                rows={3}
                className="bg-white"
              />
            </div>

            <Button onClick={handleSaveNotes} className="w-full">
              Save Notes
            </Button>

            {/* Comments */}
            <div className="border-t pt-4">
              <h4 className="font-medium text-slate-900 mb-3">Comments</h4>
              
              {ticket.comments?.length > 0 && (
                <div className="space-y-3 mb-4">
                  {ticket.comments.map((comment) => (
                    <div 
                      key={comment.id} 
                      className={`rounded-lg p-3 ${comment.is_internal ? 'bg-yellow-50 border border-yellow-200' : 'bg-slate-50'}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium text-sm">{comment.author_name}</span>
                        {comment.is_internal && (
                          <Badge variant="outline" className="text-xs text-yellow-700">Internal</Badge>
                        )}
                        <span className="text-xs text-slate-500">
                          {new Date(comment.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{comment.content}</p>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Comment */}
              <div className="space-y-2">
                <Textarea
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={2}
                />
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm">
                    <Checkbox checked={isInternal} onCheckedChange={setIsInternal} />
                    Internal comment (not visible to reporter)
                  </label>
                  <Button onClick={handleAddComment} disabled={submitting || !newComment.trim()} size="sm">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4 mr-1" />}
                    Add Comment
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div>
                  <span className="text-slate-500">Reporter:</span>
                  <p className="font-medium">{ticketData.reporter_name}</p>
                </div>
                <div>
                  <span className="text-slate-500">Email:</span>
                  <p className="font-medium">{ticketData.reporter_email}</p>
                </div>
                {ticketData.project_name && (
                  <div>
                    <span className="text-slate-500">Project:</span>
                    <p className="font-medium">{ticketData.project_name}</p>
                  </div>
                )}
                {ticketData.type === 'feature' && (
                  <div>
                    <span className="text-slate-500">Votes:</span>
                    <p className="font-medium flex items-center gap-1">
                      <ThumbsUp className="h-4 w-4 text-blue-600" />
                      {ticketData.vote_count}
                    </p>
                  </div>
                )}
                {ticketData.browser_info && (
                  <div>
                    <span className="text-slate-500">Browser:</span>
                    <p className="text-xs text-slate-600 break-all">{ticketData.browser_info}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Activity Log */}
            {ticket.activity?.length > 0 && (
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {ticket.activity.map((item) => (
                      <div key={item.id} className="text-xs border-l-2 border-slate-200 pl-2">
                        <p className="text-slate-600">{item.description}</p>
                        <p className="text-slate-400">{new Date(item.created_at).toLocaleString()}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function AnnouncementDialog({ open, onOpenChange, selectedTickets, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    announcement_type: 'release_notes',
    audience_type: 'all',
    roles: [],
  });

  const handleCreate = async () => {
    if (!formData.title.trim() || !formData.content.trim()) {
      toast.error('Please fill in the title and content');
      return;
    }

    setLoading(true);
    try {
      const data = {
        title: formData.title,
        content: formData.content,
        announcement_type: formData.announcement_type,
        included_tickets: selectedTickets,
        audience_filter: {
          type: formData.audience_type,
          roles: formData.audience_type === 'by_role' ? formData.roles : [],
        }
      };

      await api.feedback.createAnnouncement(data);
      toast.success('Announcement created');
      onSuccess?.();
      onOpenChange(false);
      setFormData({
        title: '',
        content: '',
        announcement_type: 'release_notes',
        audience_type: 'all',
        roles: [],
      });
    } catch (error) {
      toast.error('Failed to create announcement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5" />
            Create Announcement
          </DialogTitle>
          <DialogDescription>
            Notify users about new features, bug fixes, or other updates
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label>Title</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., New Features Released!"
            />
          </div>

          <div>
            <Label>Type</Label>
            <Select 
              value={formData.announcement_type} 
              onValueChange={(v) => setFormData({ ...formData, announcement_type: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new_feature">New Feature</SelectItem>
                <SelectItem value="bug_fix">Bug Fix</SelectItem>
                <SelectItem value="improvement">Improvement</SelectItem>
                <SelectItem value="release_notes">Release Notes</SelectItem>
                <SelectItem value="general">General</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Audience</Label>
            <Select 
              value={formData.audience_type} 
              onValueChange={(v) => setFormData({ ...formData, audience_type: v })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Users</SelectItem>
                <SelectItem value="by_role">By Role</SelectItem>
                <SelectItem value="by_project">By Project</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Content (HTML supported)</Label>
            <Textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Write your announcement content here..."
              rows={6}
            />
          </div>

          {selectedTickets.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                {selectedTickets.length} ticket(s) will be linked to this announcement
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={loading}>
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Megaphone className="h-4 w-4 mr-2" />}
              Create Announcement
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
